import type { SalesDraftLineItem } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TransactionAddedItemEventSource } from '../../analytics/types/analytics-events.ts';
import { useDraftContext } from './use-draft-context.ts';
import { useToasts } from './use-toasts.ts';

export type AddProductToDraftInput = {
    product_variant_id: string;
    article_group_id?: string | null;
    quantity?: number;
    quantity_unit?: string | null;
};

export type UseAddProductToDraftOptions = {
    /** Called with the newly added line item once a product has been successfully added. */
    onSuccess?: (lineItem: SalesDraftLineItem) => void;
};

export type UseAddProductToDraftHook = {
    addProduct: (product: AddProductToDraftInput, source: TransactionAddedItemEventSource) => void;
    isAdding: boolean;
};

export function useAddProductToDraft({
    onSuccess: onLineItemAdded,
}: UseAddProductToDraftOptions = {}): UseAddProductToDraftHook {
    const { draft, setDraft } = useDraftContext({ refetchOnMount: false });
    const sendEvent = useAnalytics();
    const { showUpdateErrorToast, showItemAddedToast } = useToasts();

    const { mutate, isPending } =
        api.platoUser.command.salesDraft.lineItems.createNormalLineItem.useMutation();

    function addProduct(product: AddProductToDraftInput, source: TransactionAddedItemEventSource): void {
        if (!draft) {
            return;
        }

        sendEvent(`item_added_from_${source}`, {
            article_group_id: product.article_group_id ?? undefined,
            draft_id: draft.draft_order_id,
            product_variant_id: product.product_variant_id,
            quantity: product.quantity,
        });

        mutate(
            {
                draft_order_id: draft.draft_order_id,
                product_variant_id: product.product_variant_id,
                quantity: product.quantity,
                quantity_unit: product.quantity_unit,
                shipping_type_id: draft.shipping_type_id ?? '',
                storage_id: draft.storage_id ?? '',
            },
            {
                onError: showUpdateErrorToast,
                onSuccess: (data) => {
                    setDraft(data);

                    const addedLineItem = data.draft_order.line_items.findLast(
                        (item) => item.normal_line_item,
                    );
                    if (addedLineItem) {
                        onLineItemAdded?.(addedLineItem);
                    }
                    // Mirror the previous behaviour: only the catalogue / product-detail flows (which
                    // pass an explicit quantity) surface the "item added" toast.
                    if (
                        product.quantity !== undefined &&
                        (source === 'catalogue' || source === 'product_detail_page')
                    ) {
                        showItemAddedToast({
                            quantity: product.quantity,
                            unit: product.quantity_unit ?? '',
                        });
                    }
                },
            },
        );
    }

    return { addProduct, isAdding: isPending };
}
